import { fileTypeFromBuffer } from 'file-type';

/**
 * Validates a file based on its size and type.
 *
 * This function checks if the provided file meets the following criteria:
 * 1. The file size should not exceed the maximum allowed size (in MB).
 * 2. The file type (MIME type) should match one of the allowed file types.
 *
 * @param file The file to validate.
 * @param ALLOWED_FILE_TYPES An array of allowed file types (extensions).
 * @param MAX_FILE_SIZE_MB The maximum allowed file size in MB.
 *
 * @returns An array of error messages. If the file is valid, the array will be empty.
 */
export const validateFile = async (
    file: File,
    ALLOWED_FILE_TYPES: string[],
    MAX_FILE_SIZE_MB: number
) => {
    const errors: string[] = [];

    // Taille du fichier
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
        errors.push(`La taille du fichier dépasse ${MAX_FILE_SIZE_MB} Mo.`);
    }

    // Extraction de l'extension à partir du nom
    const originalExtension = file.name.split(".").pop()?.toLowerCase();

    // Lecture et détection du type MIME
    const fileBuffer = await file.arrayBuffer();
    const detectedType = await fileTypeFromBuffer(new Uint8Array(fileBuffer));
    const detectedExtension = detectedType?.ext;
    const detectedMime = detectedType?.mime;

    // Vérification stricte des extensions
    if (!originalExtension || !ALLOWED_FILE_TYPES.includes(originalExtension)) {
        errors.push(`L'extension .${originalExtension || "inconnue"} n'est pas autorisée.`);
    }

    // Vérification stricte des types MIME et extensions détectées
    const mimeTypeMapping: Record<string, string> = {
        pdf: "application/pdf",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    };

    if (
        !detectedExtension ||
        !mimeTypeMapping[detectedExtension] ||
        detectedMime !== mimeTypeMapping[detectedExtension]
    ) {
        errors.push("Le type MIME détecté ne correspond pas à un format autorisé.");
    }

    return errors;
};
